import { CustomResponeData } from 'axios'
import { reportError } from '@zhenguo/jssdk/owl'
import { lxMV } from '@zhenguo/jssdk/lx'
import http from '@zhenguo/jssdk/axios'
import { getRiskControlInfo } from '@zhenguo/jssdk/risk-control-info'
import { getExtensionsStr, APPLY_TYPE, CERT_STATUS, AUDIT_STATUS, DEGREE, CERT_TYPE } from '../utils'
import { CUSTOMER_SERVICE_APPKEY, getCustomerServiceUrl } from '@zhenguo/jssdk/api/customer'

interface University {
  id: number
  universityCode: string
  name: string
  namePinyin: string
  provinceId: number
}

const cacheSearch: Record<string, University[]> = {}
const cacheUniversity: Record<number, Record<string, University[]>> = {}

interface FORM_DATA {
  accountName: string
  identityNo: string
  degree: number
  startYear: number
  applyType: number
  universityId?: number
  universityName?: string
  pictures?: string[]
}

interface IDENTITY_INFO {
  verifyStatus: number
  bankCardNum?: number
  name: string
  identityNo: string
  mobile: string
  canReVerify: boolean
  reVerifyAlter: string
  certType: number
}

interface Education {
  userId: number
  degree: number
  universityId: number
  universityName: string
  startYear: number
  additionalType: number
  certificationStatus: number
  certificationType: number
}

interface EduAuditInfo {
  auditId: number
  type: number
  pictures: string[]
  auditStatus: number
  reasons?: string[]
  // 审核创建时间
  gmtCreate: number
}

interface EduAuditVerifyInfo {
  userId: number
  pictures?: string[]
}

interface VERIFY_INFO_ALL {
  university: University
  education: Education
  completeVerified: boolean
  eduAuditVerifyInfo: EduAuditVerifyInfo
  eduAuditInfo: EduAuditInfo
}

interface VERIFY_INFO {
  certificationStatus: CERT_STATUS
  certificationType: CERT_TYPE
  certPending: boolean
  auditPending: boolean
  completeVerified: boolean
  startYear: number
  degree: number
  university: Pick<VERIFY_INFO_ALL['university'], 'id' | 'name'>
  pictures?: string[]
  reasons?: string[]
  // 审核创建时间
  gmtCreate: number
}

export default {
  /**
   *  实名认证
   */
  identificationVerify(data: { accountName: string; certType: number; identityNo: string }) {
    return http.post('/user/api/v1/verify', data, {
      throttle: true,
    })
  },
  // 进行学生认证
  updateVerifyInfo(data: FORM_DATA, source = 2) {
    return getRiskControlInfo('/user/api/v1/edu/info/update', window.$mpoauth).then(
      ({ fingerprint, openid, wxAccountId }) => {
        return http.post(
          '/user/api/v1/edu/info/update',
          {
            ...data,
            source,
            origin: 0,
            extensions: getExtensionsStr(source),
            fingerprint,
            openId: openid,
            wxAccountId,
          },
          {
            throttle: true,
            report: true,
          },
        )
      },
    )
  },
  /**
   * 获取学校
   * @params {Number} provinceId
   * @params {Number} namePrefix
   * 参数二选一
   */
  universityList(data: { provinceId?: number; namePrefix?: string }) {
    if (!data.provinceId && data.provinceId !== 0 && !data.namePrefix) return []
    const provinceId = data.provinceId || 0
    if (cacheUniversity[provinceId]) return cacheUniversity[provinceId]
    return http.post<CustomResponeData<University[]>>('/user/api/v1/edu/university/query', data).then((res) => {
      cacheUniversity[provinceId] = res.reduce((prev: Record<string, University[]>, cur) => {
        const letter = cur.namePinyin[0] || ''
        if (prev[letter]) {
          prev[letter].push(cur)
        } else {
          prev[letter] = [cur]
        }
        return prev
      }, {})
      return cacheUniversity[provinceId]
    })
  },
  universityListByKey(namePrefix: string) {
    if (cacheSearch[namePrefix]) return cacheSearch[namePrefix]
    return http
      .post<CustomResponeData<University[]>>(
        '/user/api/v1/edu/university/query',
        {
          namePrefix,
        },
        { cache: true },
      )
      .then((res) => {
        cacheSearch[namePrefix] = res.slice(0, 20)
        return cacheSearch[namePrefix]
      })
  },
  eduOptsInfo() {
    return http.get('/user/api/v1/edu/opts', {
      cache: true,
    })
  },
  upload(pictures: string[]) {
    if (!pictures || pictures.length === 0) {
      reportError({
        name: '图片选择错误',
      })
      return window.$toasted.show('未选择图片')
    }
    return http.post(
      '/user/api/v1/edu/auditInfo/upload',
      {
        pictures: pictures.map((i) => i.split('?')[0]),
      },
      {
        throttle: true,
        report: true,
      },
    )
  },
  // 获取学生认证信息
  userEduInfo(): Promise<VERIFY_INFO> {
    return http.get<CustomResponeData<VERIFY_INFO_ALL>>('/user/api/v1/edu/info/all').then((verifyInfo) => {
      const { education, university, eduAuditInfo, completeVerified } = verifyInfo || {}
      const { startYear, degree, certificationStatus, certificationType, universityId, universityName } =
        education || {}
      const { auditStatus, pictures, reasons, gmtCreate } = eduAuditInfo || {}
      // 认证状态 https://km.sankuai.com/page/384104173
      const defaultReturn = {
        certificationStatus,
        certificationType,
        // certPending: true, auditPending: false,
        certPending: certificationStatus === CERT_STATUS.PENDING,
        auditPending: auditStatus === AUDIT_STATUS.PENDING,
        completeVerified,
        startYear,
        degree,
        university:
          !university && universityName
            ? {
                id: universityId,
                name: universityName,
              }
            : university,
        pictures: pictures && pictures.length > 0 ? pictures : [],
        reasons,
        gmtCreate,
      }
      return defaultReturn
    })
  },
  // 获取实名、学生认证信息
  userInfoAll() {
    return Promise.all([
      http.get<CustomResponeData<IDENTITY_INFO>>('/user/api/v1/verifyInfo'),
      this.userEduInfo(),
    ]).then(([identityInfo, verifyInfo]: [IDENTITY_INFO, VERIFY_INFO]) => {
      const { name, identityNo, verifyStatus, reVerifyAlter, canReVerify } = identityInfo
      const defaultReturn = {
        name,
        identityNo,
        hasIdentified: verifyStatus === 5,
        reVerifyAlter,
        canReVerify,
        ...verifyInfo,
      }
      return defaultReturn
    })
  },
  async verify(formData: FORM_DATA, source: number | undefined) {
    const userInfo = await this.userInfoAll()
    const { accountName, identityNo, degree, universityId, universityName, startYear, applyType, pictures } = formData
    if (!userInfo.hasIdentified) {
      if (identityNo.indexOf('****') !== -1) {
        reportError({ name: '学生认证错误' })
      } else {
        const CERT_TYPE = 1001
        // 未实名认证先进行实名认证
        const identificationInfo = {
          accountName: accountName.replace(/\s+/g, ''),
          identityNo,
          certType: CERT_TYPE,
        }
        // 先实名认证
        await this.identificationVerify(identificationInfo)
        lxMV('b_airbnb_nxr99o04_mv')
      }
    }
    // 学生认证
    if (!userInfo.completeVerified) {
      let verifyInfo = {}
      // 在校生
      if (applyType === APPLY_TYPE.NORMAL) {
        verifyInfo = {
          degree,
          startYear,
          universityId,
          universityName,
        }
      } else if (applyType === APPLY_TYPE.HIGHSCHOOL) {
        // 高考生
        verifyInfo = {
          degree: DEGREE.HIGHSCHOOL,
          startYear: new Date().getFullYear(),
          universityId: 0,
          pictures: (pictures || []).map((i) => i.split('?')[0]),
        }
      }
      await this.updateVerifyInfo(verifyInfo as FORM_DATA, source)
    }
  },
  // 咨询客服
  getCSCUrl() {
    return getCustomerServiceUrl({
      appKey: CUSTOMER_SERVICE_APPKEY['edu'],
      pageSource: 1,
    })
  },
}
